import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  MenuBook as SourceIcon,
} from '@mui/icons-material';
import type { Source } from '../types/api';
import { useSources } from '../contexts/SourcesContext';
import { GekkoApiError } from '../utils/gekkoApi';

export interface SourcesModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** Array of source IDs to display */
  sourceIds: number[];
}

const SourcesModal: React.FC<SourcesModalProps> = ({
  open,
  onClose,
  sourceIds
}) => {
  const { ensureSources } = useSources();
  const [sources, setSources] = React.useState<Source[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch source information when modal opens or source IDs change
  React.useEffect(() => {
    const fetchSources = async () => {
      if (!open || sourceIds.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch sources by IDs using the context (with caching)
        const sourcesData = await ensureSources(sourceIds);
        setSources(sourcesData);
      } catch (err) {
        let errorMessage = 'Failed to load source information. Please try again.';

        if (err instanceof GekkoApiError) {
          if (err.statusCode === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (err.statusCode === 403) {
            errorMessage = 'Access denied. You may not have permission to view sources.';
          } else {
            errorMessage = err.message;
          }
        }

        setError(errorMessage);
        console.error('Error fetching sources:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSources();
  }, [open, sourceIds, ensureSources]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!open) {
      setSources([]);
      setError(null);
    }
  }, [open]);



  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '300px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SourceIcon color="primary" />
          <Typography variant="h6" component="span">
            Sources ({sourceIds.length})
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}
        
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && sources.length === 0 && (
          <Alert severity="info">
            No source information available.
          </Alert>
        )}
        
        {!loading && !error && sources.length > 0 && (
          <List>
            {sources.map((source, index) => (
              <React.Fragment key={source.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" component="span">
                        {source.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        Source ID: {source.id}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < sources.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SourcesModal;
